import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import { SpecialShift } from "./useSpecialShift";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Transport } from "../../data/api/types/driver";
import moment, { Moment } from "moment";
import {
  usePostSpecialShiftMutation,
  usePutSpecialShiftMutation,
} from "../../data/api/hooks";
import { QueryError } from "../QueryError";
import { SHIFT2_SECONDS } from "../reports/useTimetables_ByDate";

// Час за TimePicker от секунди от началото на деня (може и над 24 ч.)
function timeFromSeconds(seconds: number): Moment {
  const daySeconds = seconds % 86400;
  return moment({
    hour: Math.floor(daySeconds / 3600),
    minute: Math.floor((daySeconds % 3600) / 60),
  });
}

// Секунди от началото на деня; null, докато въведеното не е валиден час
function secondsFromTime(time: Moment | null): number | null {
  return time && time.isValid()
    ? time.hours() * 3600 + time.minutes() * 60
    : null;
}

export function SpecialShiftEditForm({
  open,
  setOpen,
  transport,
  depot,
  autocolumn,
  selectedShift,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  transport: Transport;
  depot: number;
  autocolumn: number;
  selectedShift: SpecialShift | undefined;
}) {
  const [value, setValue] = useState<SpecialShift>({
    transport: transport,
    depot_id: depot,
    auto_fill: true,
    auto_fill_count: 0,
    description: "",
    id: -1,
    end_time: 0,
    start_time: 0,
    shift_type: 1,
    validity_start: moment(),
    validity_end: null,
    autocolumn: autocolumn,
  });

  const [isChanged, setIsChanged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [addSpecialShift, resultAddSpecialShift] =
    usePostSpecialShiftMutation();

  const [editSpecialShift, resultEditSpecialShift] =
    usePutSpecialShiftMutation();

  const handleClose = useCallback(() => {
    if (!isChanged) {
      console.log("Close");
      setIsChanged(false);
      setOpen(false);
    } else {
      if (
        window.confirm(
          "Направените промени няма да бъдат записани. Желаете ли да продължите?"
        )
      ) {
        setIsChanged(false);
        setOpen(false);
      }
    }
  }, [isChanged, setOpen]);

  useEffect(() => {
    if (
      (resultAddSpecialShift.isSuccess || resultEditSpecialShift.isSuccess) &&
      isSaving
    ) {
      console.log("useEffect Close", resultAddSpecialShift.isSuccess, isSaving);
      setIsChanged(false);
      setIsSaving(false);
      setOpen(false);
    }
  }, [
    handleClose,
    isSaving,
    resultAddSpecialShift.isSuccess,
    resultEditSpecialShift.isSuccess,
    setOpen,
  ]);

  // Само при отваряне на диалога - със value в зависимостите всяко
  // setValue() тук пускаше ефекта отново и формата се рисуваше и нулираше
  // непрекъснато, докато е отворена
  useEffect(() => {
    if (open) {
      setIsChanged(false);
      if (selectedShift) setValue(selectedShift);
      else
        setValue({
          transport: transport,
          depot_id: depot,
          auto_fill: true,
          auto_fill_count: 0,
          description: "",
          id: -1,
          end_time: 0,
          start_time: 0,
          shift_type: 1,
          validity_start: moment(),
          validity_end: null,
          autocolumn: autocolumn,
        });
    }
  }, [autocolumn, depot, open, selectedShift, transport]);

  //   useEffect(() => {
  //     if (isChanged) {
  //       const onUnload = (e: BeforeUnloadEvent) => {
  //         e.preventDefault();
  //         e.returnValue = "";
  //       };

  //       window.addEventListener("beforeunload", onUnload);
  //       return () => window.removeEventListener("beforeunload", onUnload);
  //     }
  //   }, [isChanged]);

  const handleOk = useCallback(() => {
    console.log("value:", value);
    value.id >= 0
      ? editSpecialShift({ shift: value })
      : addSpecialShift({ shift: value });
    setIsSaving(true);
    console.log("resultAddSpecialShift: ", resultEditSpecialShift);
  }, [addSpecialShift, editSpecialShift, resultEditSpecialShift, value]);

  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsChanged(true);
      const description = event.target.value;
      setValue((v) => ({ ...v, description }));
    },
    []
  );

  const handleStartChange = useCallback((newValue: Moment | null) => {
    const start_time = secondsFromTime(newValue);
    if (start_time !== null) {
      setIsChanged(true);
      setValue((v) => ({
        ...v,
        start_time,
        shift_type: start_time >= SHIFT2_SECONDS ? 2 : 1,
      }));
    }
  }, []);

  const handleEndChange = useCallback((newValue: Moment | null) => {
    const end_time = secondsFromTime(newValue);
    if (end_time !== null) {
      setIsChanged(true);
      setValue((v) => ({ ...v, end_time }));
    }
  }, []);

  const handleCountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsChanged(true);
      const auto_fill_count = Number(event.target.value);
      setValue((v) => ({ ...v, auto_fill_count }));
    },
    []
  );

  const startTime = useMemo(
    () => timeFromSeconds(value.start_time),
    [value.start_time]
  );
  const endTime = useMemo(
    () => timeFromSeconds(value.end_time),
    [value.end_time]
  );

  return (
    <Dialog open={open} onClose={handleClose} onSubmit={handleOk}>
      <DialogTitle>
        {!selectedShift
          ? "Нова специална смяна"
          : "Редакция на специална смяна"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {!selectedShift
            ? "Моля, въведете параметри за смяната."
            : "Моля редактирайте името или броя смени по подаразбиране."}
        </DialogContentText>
        {resultAddSpecialShift.isError ||
          (resultEditSpecialShift.isError && (
            <QueryError
              error={
                resultAddSpecialShift
                  ? resultAddSpecialShift.error
                  : resultEditSpecialShift.error
              }
            />
          ))}
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>
                  <label>Име</label>
                </TableCell>
                <TableCell>
                  <TextField
                    autoFocus
                    sx={{ padding: 2 }}
                    id="name"
                    type="text"
                    variant="standard"
                    multiline
                    value={value.description}
                    onChange={handleNameChange}
                  ></TextField>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <label>Начало</label>
                </TableCell>
                <TableCell>
                  <TimePicker
                    sx={{ width: 100 }}
                    disabled={selectedShift !== undefined}
                    value={startTime}
                    onChange={handleStartChange}
                  ></TimePicker>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <label>Край</label>
                </TableCell>
                <TableCell>
                  <TimePicker
                    sx={{ width: 100 }}
                    disabled={selectedShift !== undefined}
                    value={endTime}
                    onChange={handleEndChange}
                  ></TimePicker>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <label>Брой смени</label>
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={value.auto_fill_count || 0}
                    style={{ width: 100 }}
                    onChange={handleCountChange}
                  ></TextField>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button disabled={!isChanged} onClick={handleOk}>
          Запис
        </Button>
        <Button onClick={handleClose}>Изход</Button>
      </DialogActions>
    </Dialog>
  );
}
